/**
 * SaleDetailModal Component - Display complete sale details
 * Used in Sales page and Dashboard recent sales
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInstance } from '../../contexts/InstanceContext';
import { Modal } from './modal';
import { Badge } from './badge';
import { Separator } from './separator';
import { 
  Receipt, 
  Package, 
  CreditCard, 
  Wallet,
  Hash
} from 'lucide-react';
import './SaleDetailModal.css';

export function SaleDetailModal({ open, onOpenChange, sale }) {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();

  if (!sale) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-primary" />
          Detalhes da Venda
        </span>
      }
      description={`Venda #${sale.sale_number || sale.id?.slice(0, 8)}`}
      size="default"
      showConfirm={false}
      cancelText="Fechar"
      onCancel={() => onOpenChange(false)}
    >
      <div className="sale-detail-content space-y-4">
        {/* Sale Info */}
        <div className="sale-detail-section">
          <h3 className="sale-detail-section-title">
            <Hash className="h-4 w-4" />
            Informações da Venda
          </h3>
          <div className="sale-detail-grid">
            <div className="sale-detail-item">
              <span className="sale-detail-label">Número</span>
              <span className="sale-detail-value">#{sale.sale_number || '-'}</span>
            </div>
            <div className="sale-detail-item">
              <span className="sale-detail-label">Data/Hora</span>
              <span className="sale-detail-value">{formatDate(sale.created_at)}</span>
            </div>
            <div className="sale-detail-item">
              <span className="sale-detail-label">Status</span>
              <Badge variant={sale.status === 'completed' ? 'default' : 'secondary'}>
                {sale.status === 'completed' ? 'Finalizada' : sale.status}
              </Badge>
            </div>
          </div>
        </div>

        <Separator />

        {/* Cashier & Attendant */}
        <div className="sale-detail-section">
          <h3 className="sale-detail-section-title">
            <Wallet className="h-4 w-4" />
            Caixa e Atendente
          </h3>
          <div className="sale-detail-grid">
            <div className="sale-detail-item">
              <span className="sale-detail-label">Caixa</span>
              <span className="sale-detail-value">{sale.cashier_name || sale.cashier_id?.slice(0, 8) || '-'}</span>
            </div>
            <div className="sale-detail-item">
              <span className="sale-detail-label">Atendente</span>
              <span className="sale-detail-value">{sale.user_name || sale.created_by?.slice(0, 8) || '-'}</span>
            </div>
            {sale.table_number && (
              <div className="sale-detail-item">
                <span className="sale-detail-label">Mesa</span>
                <span className="sale-detail-value">Mesa {sale.table_number}</span>
              </div>
            )}
            {sale.customer_name && (
              <div className="sale-detail-item">
                <span className="sale-detail-label">Cliente</span>
                <span className="sale-detail-value">{sale.customer_name}</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Products */}
        <div className="sale-detail-section">
          <h3 className="sale-detail-section-title">
            <Package className="h-4 w-4" />
            Produtos ({sale.items?.length || 0})
          </h3>
          <div className="sale-detail-items">
            {sale.items?.map((item, idx) => (
              <div key={idx} className="sale-detail-product">
                <div className="sale-detail-product-info">
                  <span className="sale-detail-product-qty">{item.quantity}x</span>
                  <span className="sale-detail-product-name">{item.name || item.product_name}</span>
                  {item.variation_name && (
                    <span className="sale-detail-product-variation">({item.variation_name})</span>
                  )}
                </div>
                <div className="sale-detail-product-prices">
                  <span className="sale-detail-product-unit">
                    {formatCurrency(item.unit_price)} un.
                  </span>
                  <span className="sale-detail-product-total">
                    {formatCurrency((item.unit_price || 0) * (item.quantity || 1))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Payments */}
        <div className="sale-detail-section">
          <h3 className="sale-detail-section-title">
            <CreditCard className="h-4 w-4" />
            Pagamentos ({sale.payments?.length || 0})
          </h3>
          <div className="sale-detail-payments">
            {sale.payments?.map((payment, idx) => (
              <div key={idx} className="sale-detail-payment">
                <span className="sale-detail-payment-method">
                  {payment.payment_method_name || 'Pagamento'}
                  {payment.payer_name && (
                    <span className="sale-detail-payment-payer"> - {payment.payer_name}</span>
                  )}
                </span>
                <span className="sale-detail-payment-amount">
                  {formatCurrency(payment.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Totals */}
        <div className="sale-detail-section">
          <h3 className="sale-detail-section-title">Totais</h3>
          <div className="sale-detail-totals">
            <div className="sale-detail-total-row">
              <span>Subtotal</span>
              <span>{formatCurrency(sale.subtotal || 0)}</span>
            </div>
            {sale.discount > 0 && (
              <div className="sale-detail-total-row sale-detail-discount">
                <span>Desconto</span>
                <span>- {formatCurrency(sale.discount)}</span>
              </div>
            )}
            {sale.service_fee > 0 && (
              <div className="sale-detail-total-row">
                <span>Taxa de Serviço</span>
                <span>+ {formatCurrency(sale.service_fee)}</span>
              </div>
            )}
            <div className="sale-detail-total-row sale-detail-grand-total">
              <span>Total</span>
              <span>{formatCurrency(sale.total || 0)}</span>
            </div>
            {sale.change > 0 && (
              <div className="sale-detail-total-row sale-detail-change">
                <span>Troco</span>
                <span>{formatCurrency(sale.change)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {sale.notes && (
          <>
            <Separator />
            <div className="sale-detail-section">
              <h3 className="sale-detail-section-title">Observações</h3>
              <p className="sale-detail-notes">{sale.notes}</p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default SaleDetailModal;
